/**
 * Image URL Helper - Centralized R2 CDN Image Management
 * 
 * This module provides utilities for managing image URLs from the Cloudflare R2 CDN
 * with automatic cache-busting to ensure users always get the latest versions.
 */

// R2 CDN Base URL
export const CDN_BASE_URL = 'https://cdn.educ4te.com';

// Image version for cache-busting (update when images change)
// Format: YYYYMMDD or increment version number
const IMAGE_VERSION = '20260102';

/**
 * Get a cache-busted image URL from R2 CDN
 * @param {string} path - Image path relative to CDN root (e.g., '/courses/course-1.webp')
 * @param {boolean} withVersion - Whether to include version query parameter (default: true)
 * @returns {string} Full CDN URL with optional cache-busting parameter
 */
export function getCdnImageUrl(path, withVersion = true) {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const baseUrl = `${CDN_BASE_URL}${normalizedPath}`;

  return withVersion ? `${baseUrl}?v=${IMAGE_VERSION}` : baseUrl;
}

/**
 * Image path constants for commonly used images
 * All paths use WebP format for optimal performance
 */
export const ImagePaths = {
  // Logos
  LOGO_MAIN: getCdnImageUrl('/logos/main-logo.webp'),
  LOGO_NAVBAR: getCdnImageUrl('/logos/purple_orange_lion_on_white.webp'),
  LOGO_FOOTER: getCdnImageUrl('/logos/white_lion_on_purple.webp'),
  LOGO_MARK_DARK: getCdnImageUrl('/educ4te.logos/logo-mark-lion-dark.webp'),
  
  // Lion Logo Variations
  LION_2: getCdnImageUrl('/logos/2.webp'),
  LION_3: getCdnImageUrl('/logos/3.webp'),
  LION_4: getCdnImageUrl('/logos/4.webp'),
  LION_5: getCdnImageUrl('/logos/5.webp'),
  LION_6: getCdnImageUrl('/logos/6.webp'),
  LION_7: getCdnImageUrl('/logos/7.webp'),
  LION_8: getCdnImageUrl('/logos/8.webp'),
  LION_9: getCdnImageUrl('/logos/9.webp'),
  LION_10: getCdnImageUrl('/logos/10.webp'),
  LION_11: getCdnImageUrl('/logos/11.webp'),
  
  
  // Courses
  COURSE_1: getCdnImageUrl('/courses/course-1.webp'),
  COURSE_2: getCdnImageUrl('/courses/course-2.webp'),
  COURSE_3: getCdnImageUrl('/courses/course-3.webp'),
  COURSE_4: getCdnImageUrl('/courses/course-4.webp'),
  
  // Assessments (placeholders until real images created)
  // Assessment hero images — point at locally-bundled OG PNGs (cdn.educ4te.com
  // /assessments/*.webp returns 404 site-wide; the OG PNGs are committed under
  // public/images/og/ and ship with the build). Repoint here when R2 has the
  // brand-correct hero artwork uploaded.
  ASSESSMENT_M365_HEALTH: '/images/og/microsoft-365-health-check-1200x630.png',
  ASSESSMENT_IDENTITY_ACCESS: '/images/og/identity-assessment-1200x630.png',
  ASSESSMENT_AD_RESILIENCE: '/images/og/active-directory-assessment-1200x630.png',
  ASSESSMENT_AI_COPILOT: '/images/og/copilot-readiness-1200x630.png',
  ASSESSMENT_ESSENTIAL_EIGHT: '/images/og/essential-eight-maturity-1200x630.png',
  ASSESSMENT_SMB_CYBER: '/images/og/smb-cyber-security-1200x630.png',
  ASSESSMENT_SHAREPOINT_TEAMS: '/images/og/sharepoint-teams-governance-1200x630.png',
  ASSESSMENT_COPILOT_DEPLOYMENT: '/images/og/copilot-deployment-1200x630.png',
  ASSESSMENT_COPILOT_GOVERNANCE: '/images/og/copilot-governance-1200x630.png',
  ASSESSMENT_COPILOT_E8_ALIGNMENT: '/images/og/copilot-essential-eight-alignment-1200x630.png',
  
  // Testimonials
  TESTIMONIAL_1: getCdnImageUrl('/testimonials/testimonial-1.webp'),
  TESTIMONIAL_2: getCdnImageUrl('/testimonials/testimonial-2.webp'),
  TESTIMONIAL_3: getCdnImageUrl('/testimonials/testimonial-3.webp'),
  
  // Team
  TEAM_ALPESH: getCdnImageUrl('/team/alpesh-nakar.webp'),
  
};

/**
 * Update image version for cache invalidation
 * Call this function when images are updated to force browser cache refresh
 * @param {string} newVersion - New version string (e.g., '20251219' or 'v2')
 */
export function updateImageVersion(newVersion) {
  console.warn('Image version should be updated in imageUrls.js file. Current:', IMAGE_VERSION, 'Requested:', newVersion);
}

export default {
  getCdnImageUrl,
  ImagePaths,
  CDN_BASE_URL,
};
